import { useId } from "react";
import { inputClass, labelClass } from "../../../../constants/formStyles.ts";
import type { useBlankInventory } from "../useBlankInventory.ts";

const freeCount = (count: number) =>
  count === 1 ? "1 свободен" : `${count} свободни`;

/**
 * Blanks are picked from the free numbers Wing holds for the chosen insurer
 * agency and agent, never typed, so a used or another agency's blank cannot be sent.
 */
export function BlankPicker({
  form,
}: {
  form: ReturnType<typeof useBlankInventory>;
}) {
  const id = useId();
  const { inventory, ready, blanks, setBlanks, loading, error } = form;
  return (
    <div>
      {!ready && (
        <p className="text-sm">
          Изберете агенция на застрахователя и агент, за да се заредят
          свободните бланки.
        </p>
      )}
      {loading && (
        <p role="status" className="text-sm">
          Зареждане на наличните бланки…
        </p>
      )}
      {Boolean(error) && (
        <p role="alert" className="text-sm text-danger">
          Бланките не се заредиха.{" "}
          <button type="button" onClick={form.refresh} className="underline">
            Опитай отново
          </button>
        </p>
      )}
      <fieldset
        disabled={!inventory || loading || Boolean(error)}
        className="grid grid-cols-2 gap-2 sm:grid-cols-4"
      >
        {(["sticker", "green-card"] as const).map((kind) => {
          const noun = kind === "sticker" ? "стикер" : "сертификат";
          const seriesKey =
            kind === "sticker" ? "stickerSeries" : "greenCardSeries";
          const numberKey =
            kind === "sticker" ? "stickerNumber" : "greenCardNumber";
          const rows = (inventory?.[kind] ?? []).filter(
            (row) => kind === "sticker" || row.series.trim()
          );
          // Stickers usually have no series; a single series needs no choice.
          const series =
            blanks[seriesKey] ??
            (rows.some((row) => row.series === "")
              ? ""
              : rows.length === 1
              ? rows[0].series
              : undefined);
          const chosen = rows.find((row) => row.series === series);
          const number = blanks[numberKey] ?? "";
          // An optional blank can be cleared again; a required one cannot.
          const optional = form.optional[kind];
          const seriesId = `${id}-${seriesKey}`,
            numberId = `${id}-${numberKey}`;
          return (
            <div key={kind} className="contents">
              <div>
                <label htmlFor={seriesId} className={labelClass}>
                  Серия {noun}
                </label>
                <select
                  id={seriesId}
                  className={inputClass}
                  disabled={!rows.length}
                  value={chosen?.series ?? ""}
                  onChange={(event) => {
                    const next = rows.find(
                      (row) => row.series === event.target.value
                    );
                    setBlanks({
                      ...blanks,
                      [seriesKey]: event.target.value,
                      [numberKey]: next?.nextAvailable ?? "",
                    });
                  }}
                >
                  {!rows.length ? (
                    <option value="" disabled>
                      {inventory ? "Няма свободни" : "—"}
                    </option>
                  ) : (
                    !rows.some((row) => row.series === "") && (
                      <option value="" disabled hidden>
                        — изберете серия —
                      </option>
                    )
                  )}
                  {rows.map((row) => (
                    <option key={row.series} value={row.series}>
                      {row.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor={numberId} className={labelClass}>
                  Номер {noun}
                </label>
                <select
                  id={numberId}
                  className={inputClass}
                  disabled={!chosen}
                  aria-describedby={inventory ? `${numberId}-free` : undefined}
                  value={chosen?.available.includes(number) ? number : ""}
                  onChange={(event) =>
                    setBlanks({
                      ...blanks,
                      [seriesKey]: chosen?.series ?? "",
                      [numberKey]: event.target.value,
                    })
                  }
                >
                  {optional ? (
                    <option value="">{`— без ${noun} —`}</option>
                  ) : (
                    <option value="" disabled hidden>
                      {chosen ? "— изберете номер —" : "—"}
                    </option>
                  )}
                  {chosen?.available.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
                {inventory && (
                  <p id={`${numberId}-free`} className="mt-1 text-xs">
                    {chosen
                      ? freeCount(chosen.available.length)
                      : rows.length
                      ? "Изберете серия"
                      : "Няма свободни за агента"}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </fieldset>
    </div>
  );
}
